import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * StudioLayout — the region shell for the TouchTour Studio node editor.
 *
 * It owns nothing but layout: it arranges the five studio regions and renders
 * whatever each work package passes in. Empty slots show a labelled placeholder
 * so the regions other WPs will fill stay visible during development.
 *
 * Slots:
 *   - toolbar:   top bar            (WP-A StudioToolbar)
 *   - left:      scenes panel       (WP-C ScenesPanel) — may be an absolute overlay
 *   - center:    the canvas         (TourCanvas) — required
 *   - right:     inspector          (WP-B StudioInspector / WP-E FiltersPanel)
 *   - statusBar: bottom status bar  (WP-F EditorAlerts, tools, zoom)
 *
 * The Canvas API (fitToView/fitToImages/jumpTo/panBy/zoomBy/animateTo/
 * getCameraState) is exposed by TourCanvas via its `onReady` callback and held
 * by NodeEditor — panels mounted in these slots consume it from there, they do
 * not reach into TourCanvas internals.
 */

const RIGHT_WIDTH = 220;
const RIGHT_RAIL = 28;
const TOGGLE_SIZE = 28;

const useStyles = createUseStyles(theme => ({
  root: {
    width: "100%",
    // Bound the shell to the viewport so the body (and the scroll regions
    // inside each panel, e.g. the scenes list) scroll internally instead of
    // growing the whole page when their content is tall.
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.colorBackground,
    overflow: "hidden",
  },
  toolbar: {
    flex: "0 0 auto",
    position: "relative",
    zIndex: 100,
  },
  body: {
    position: "relative",
    flex: 1,
    display: "flex",
    minHeight: 0,
    overflow: "hidden",
  },
  center: {
    position: "relative",
    flex: 1,
    minWidth: 0,
    display: "flex",
  },
  right: {
    position: "relative",
    flex: `0 0 ${RIGHT_WIDTH}px`,
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.colorBackground,
    borderLeft: `1px solid ${theme.colorDarkGrey}`,
    minWidth: 0,
  },
  rightCollapsed: {
    flex: `0 0 ${RIGHT_RAIL}px`,
  },
  rightInner: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  rightToggle: {
    position: "absolute",
    top: "50%",
    zIndex: 200,
    width: TOGGLE_SIZE,
    height: TOGGLE_SIZE * 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.textColor,
    backgroundColor: theme.colorDarkGrey,
    border: `1px solid ${theme.colorGrey}`,
    borderRight: "none",
    borderRadius: `${theme.borderRadius} 0 0 ${theme.borderRadius}`,
    cursor: "pointer",
    padding: 0,
    fontSize: 14,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.45)",
    transform: "translateY(-50%)",
    transitionProperty: "color, background-color, border-color",
    transitionDuration: theme.transitionDuration,
    "&:hover": {
      color: theme.colorPrimary,
      backgroundColor: theme.colorBlack,
      borderColor: theme.colorPrimary,
    },
  },
  statusBar: {
    flex: "0 0 auto",
  },
  placeholder: {
    flex: 1,
    margin: theme.spacing2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.colorGrey,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 1,
    border: `1px dashed ${theme.colorDarkGrey}`,
    borderRadius: theme.borderRadius,
    minHeight: 40,
  },
}));

const Placeholder = ({ classes, label }) => (
  <div className={classes.placeholder}>{label}</div>
);

const StudioLayout = ({ toolbar, left, center, right, statusBar }) => {
  const classes = useStyles();
  const [rightCollapsed, setRightCollapsed] = useState(false);

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        {toolbar ?? <Placeholder classes={classes} label="Toolbar" />}
      </div>
      <div className={classes.body}>
        {left}
        <div className={classes.center}>{center}</div>
        <button
          type="button"
          className={classes.rightToggle}
          style={{
            right: rightCollapsed ? RIGHT_RAIL : RIGHT_WIDTH,
          }}
          onClick={() => setRightCollapsed(collapsed => !collapsed)}
          title={rightCollapsed ? "Show inspector" : "Hide inspector"}
          aria-expanded={!rightCollapsed}
        >
          <FontAwesomeIcon
            icon={["fal", rightCollapsed ? "chevron-left" : "chevron-right"]}
          />
        </button>
        <div
          className={clsx(classes.right, {
            [classes.rightCollapsed]: rightCollapsed,
          })}
        >
          {!rightCollapsed && (
            <div className={classes.rightInner}>
              {right ?? <Placeholder classes={classes} label="Inspector" />}
            </div>
          )}
        </div>
      </div>
      <div className={classes.statusBar}>
        {statusBar ?? <Placeholder classes={classes} label="Status bar" />}
      </div>
    </div>
  );
};

export default StudioLayout;
